/**
 * Batch management API — CRUD, upload, profiling, semantic summaries, source preview.
 */
import axios from 'axios';
import {
  apiClient,
  buildUrl,
  ensureProjectId,
  coerceNumber,
  coerceOptionalNumber,
  bytesToMegabytes,
} from './apiClient';
import type {
  Batch,
  Profile,
  ColumnProfile,
  PromptLogEntry,
  MappingResponse,
  SemanticSummaryResponse,
  SourcePreview,
  DashboardStats,
} from './types';

// --- Profile mapping helpers ---

const mapHistogram = (raw: any): { [key: string]: number } | undefined => {
  if (!raw) {
    return undefined;
  }

  if (Array.isArray(raw?.bins) && Array.isArray(raw?.counts)) {
    const histogram: Record<string, number> = {};
    raw.bins.forEach((bin: any, index: number) => {
      const key = String(bin);
      histogram[key] = coerceNumber(raw.counts[index], 0);
    });
    return histogram;
  }

  if (typeof raw === 'object') {
    return Object.entries(raw).reduce<Record<string, number>>((acc, [key, value]) => {
      acc[key] = coerceNumber(value, 0);
      return acc;
    }, {});
  }

  return undefined;
};

const mapColumnProfile = (raw: any): ColumnProfile => {
  const sampleSource = Array.isArray(raw?.sample_values)
    ? raw.sample_values
    : Array.isArray(raw?.samples)
      ? raw.samples
      : [];

  const sampleValues = sampleSource
    .filter((value: any) => value !== undefined && value !== null)
    .map((value: any) => String(value));

  const nullPercentage = coerceNumber(raw?.null_percentage ?? raw?.nullPercent, 0);
  const uniqueCount = Math.max(0, Math.round(coerceNumber(raw?.unique_count ?? raw?.cardinality, 0)));

  const avgLength =
    raw?.avg_length !== undefined && raw?.avg_length !== null
      ? coerceOptionalNumber(raw.avg_length)
      : raw?.average_length !== undefined && raw?.average_length !== null
        ? coerceOptionalNumber(raw.average_length)
        : undefined;

  const confidenceRaw = raw?.confidence_score ?? raw?.pii_confidence;
  const confidenceScore =
    confidenceRaw === undefined || confidenceRaw === null
      ? undefined
      : Math.max(0, Math.min(1, coerceNumber(confidenceRaw, 0)));

  return {
    column_name:
      typeof raw?.column_name === 'string' && raw.column_name.trim().length > 0
        ? raw.column_name
        : typeof raw?.name === 'string' && raw.name.trim().length > 0
          ? raw.name
          : 'Unnamed column',
    data_type:
      typeof raw?.data_type === 'string'
        ? raw.data_type
        : typeof raw?.inferred_type === 'string'
          ? raw.inferred_type
          : 'unknown',
    null_percentage: nullPercentage,
    unique_count: uniqueCount,
    sample_values: sampleValues,
    min_value: raw?.min_value ?? raw?.minimum,
    max_value: raw?.max_value ?? raw?.maximum,
    avg_length: avgLength,
    histogram: mapHistogram(raw?.histogram),
    pii_detected: Boolean(raw?.pii_detected ?? raw?.contains_pii ?? false),
    confidence_score: confidenceScore,
  };
};

const resolveFileName = (raw: any): string => {
  if (typeof raw?.file_name === 'string' && raw.file_name.trim().length > 0) {
    return raw.file_name;
  }
  if (typeof raw?.file_key === 'string' && raw.file_key.trim().length > 0) {
    return raw.file_key;
  }
  if (typeof raw?.source_uri === 'string' && raw.source_uri.trim().length > 0) {
    const parts = raw.source_uri.split('/');
    const candidate = parts[parts.length - 1];
    return candidate || raw.source_uri;
  }
  return 'Unnamed file';
};

const mapProfileResponse = (payload: any): Profile[] => {
  if (!payload) {
    return [];
  }

  const rawProfiles = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.profiles)
      ? payload.profiles
      : [];

  if (!Array.isArray(rawProfiles)) {
    return [];
  }

  return rawProfiles
    .filter(Boolean)
    .map((raw: any) => {
      const columns = Array.isArray(raw?.columns)
        ? raw.columns.map((column: any) => mapColumnProfile(column))
        : [];

      const rowCount = Math.max(0, Math.round(coerceNumber(raw?.row_count ?? raw?.rows, 0)));
      const fileSizeMb =
        raw?.file_size_mb !== undefined && raw?.file_size_mb !== null
          ? coerceNumber(raw.file_size_mb, 0)
          : bytesToMegabytes(raw?.file_size_bytes);

      const batchId =
        typeof raw?.batch_id === 'string'
          ? raw.batch_id
          : typeof payload?.batch?.batch_id === 'string'
            ? payload.batch.batch_id
            : '';

      return {
        batch_id: batchId,
        file_name: resolveFileName(raw),
        columns,
        row_count: rowCount,
        file_size_mb: fileSizeMb,
        source_uri: typeof raw?.source_uri === 'string' ? raw.source_uri : undefined,
        file_format: typeof raw?.file_format === 'string' ? raw.file_format : undefined,
        profiled_at: typeof raw?.profiled_at === 'string' ? raw.profiled_at : undefined,
      };
    });
};

const buildEmptyMappingResponse = (batchId: string): MappingResponse => {
  const timestamp = new Date().toISOString();
  return {
    recipe_id: '',
    batch_id: batchId,
    client_id: '',
    status: 'draft',
    ai_confidence_score: 0,
    created_at: timestamp,
    mappings: [],
    relationships: [],
    quality_checks: [],
    metadata: {},
    transformation_plan: undefined,
    dependent_sources: [],
    sample_output: {},
    suggestions: [],
    observations: [],
    // Issue #656 / refinement: typed first-class fields on MappingResponse.
    validator_overrides: {},
    data_quality_actions: [],
  };
};

// --- Batch API methods ---

export async function getBatches(): Promise<Batch[]> {
  try {
    const url = buildUrl('/api/v1/batches');
    const response = await apiClient.get(url);
    return response.data;
  } catch (error) {
    console.error('Failed to get batches:', error);
    throw error;
  }
}

export async function getBatch(batchId: string): Promise<Batch> {
  const response = await apiClient.get(buildUrl(`/api/v1/batches/${batchId}`));
  return response.data;
}

export async function uploadFiles(files: FileList, options?: { projectId?: string; schemaSetId?: string | null }): Promise<Batch> {
  return uploadBatch(Array.from(files), options);
}

export async function getProfile(batchId: string): Promise<Profile[]> {
  try {
    const response = await apiClient.get(buildUrl(`/api/v1/batches/${batchId}/profile`));
    return mapProfileResponse(response.data);
  } catch (error) {
    console.error('Failed to get profile:', error);
    throw error;
  }
}

export async function triggerProfiling(batchId: string): Promise<void> {
  await apiClient.post(buildUrl(`/api/v1/batches/${batchId}/profile`));
}

export async function getSemanticSummaries(batchId: string): Promise<SemanticSummaryResponse> {
  const response = await apiClient.get<SemanticSummaryResponse>(buildUrl(`/api/v1/batches/${batchId}/semantic-summary`));
  return response.data;
}

export async function updateSemanticSummary(batchId: string, summary: Record<string, unknown>): Promise<SemanticSummaryResponse> {
  const response = await apiClient.put<SemanticSummaryResponse>(buildUrl(`/api/v1/batches/${batchId}/semantic-summary`), summary);
  return response.data;
}

export async function chat(
  batchId: string,
  message: string,
  context: Record<string, any>
): Promise<{ run_id: string; status: string; message: string; schema_modified?: boolean }> {
  const response = await apiClient.post(buildUrl('/api/v1/agents/chat'), {
    batch_id: batchId,
    message,
    context,
    metadata: { source: 'schema-chat' },
  });
  return response.data;
}

export interface FilePreviewColumn {
  name: string;
  type: string;
}

export interface FileSheetPreview {
  sheet_name: string;
  columns: FilePreviewColumn[];
  rows: any[][];
  row_count: number;
}

export interface FilePreviewResponse {
  file_name: string;
  /** Single-sheet files */
  columns?: FilePreviewColumn[];
  rows?: any[][];
  row_count?: number;
  /** Multi-sheet Excel files */
  is_multi_sheet?: boolean;
  sheets?: FileSheetPreview[];
}

export async function previewFile(file: File, limit: number = 10): Promise<FilePreviewResponse> {
  // For CSV/TSV files, only send the first 5 MB — the backend only reads
  // headers + `limit` rows via pd.read_csv(nrows=...), so the full file
  // is unnecessary and causes 413 / timeout errors for large files.
  const isTextTabular = /\.(csv|tsv)$/i.test(file.name);
  const MAX_PREVIEW_BYTES = 5 * 1024 * 1024; // 5 MB
  const payload =
    isTextTabular && file.size > MAX_PREVIEW_BYTES
      ? new File([file.slice(0, MAX_PREVIEW_BYTES)], file.name, { type: file.type })
      : file;

  const formData = new FormData();
  formData.append('file', payload);
  const response = await apiClient.post(
    buildUrl(`/api/v1/files/preview?limit=${limit}`),
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120_000 },
  );
  return response.data;
}

export async function getSourcePreview(
  batchId: string,
  fileName: string,
  limit: number = 25
): Promise<SourcePreview> {
  const params = new URLSearchParams();
  params.set('file_name', fileName);
  params.set('limit', String(limit));
  const response = await apiClient.get(
    buildUrl(`/api/v1/batches/${batchId}/source-preview?${params.toString()}`)
  );
  return response.data;
}

export async function getPromptLogs(batchId: string, logType?: string): Promise<{ batch_id: string; logs: PromptLogEntry[]; count: number }> {
  const params = logType ? `?log_type=${logType}` : '';
  const response = await apiClient.get(buildUrl(`/api/v1/batches/${batchId}/prompt-logs${params}`));
  return response.data;
}

export async function triggerBatchMapping(
  batchId: string,
  options?: { source_tables?: string[]; target_tables?: string[]; target_schema_id?: string },
): Promise<void> {
  await apiClient.post(buildUrl(`/api/v1/batches/${batchId}/mapping`), options ?? {});
}

export async function getTransformationStatus(batchId: string): Promise<{ status: string; progress?: number; logs?: string[] }> {
  const response = await apiClient.get(buildUrl(`/api/v1/batches/${batchId}/status`));
  return response.data;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percent: number;
}

export async function uploadBatch(
  files: File | FileList | File[],
  options: {
    projectId?: string;
    schemaId?: string | null;
    columnsByTable?: Record<string, string[]>;
    batchName?: string;
    onUploadProgress?: (progress: UploadProgress) => void;
  } = {}
): Promise<Batch> {
  const filesArray =
    files instanceof FileList ? Array.from(files) : Array.isArray(files) ? files : [files];

  if (filesArray.length === 0) {
    throw new Error('At least one file is required to create a batch upload request.');
  }

  const formData = new FormData();
  filesArray.forEach((file) => formData.append('files', file));
  formData.append('client_id', 'web-ui');
  formData.append('created_by', 'user@example.com');
  formData.append('project_id', ensureProjectId(options.projectId));
  if (options.schemaId) {
    formData.append('target_schema_id', options.schemaId.trim());
  }
  if (options.columnsByTable && Object.keys(options.columnsByTable).length > 0) {
    formData.append('columns_by_table', JSON.stringify(options.columnsByTable));
  }
  formData.append('batch_name', options.batchName || filesArray[0].name.split('.')[0]);
  const response = await apiClient.post(buildUrl('/api/v1/batches/upload'), formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 600000,
    onUploadProgress: options.onUploadProgress
      ? (e) => {
          const total = e.total ?? 0;
          options.onUploadProgress!({
            loaded: e.loaded,
            total,
            percent: total ? Math.round((e.loaded / total) * 100) : 0,
          });
        }
      : undefined,
  });
  return response.data;
}

export async function renameBatch(batchId: string, batchName: string): Promise<{ batch_id: string; batch_name: string }> {
  const response = await apiClient.patch<{ batch_id: string; batch_name: string }>(
    buildUrl(`/api/v1/batches/${batchId}/name`),
    { batch_name: batchName },
  );
  return response.data;
}

export async function createBatchFromConnector(
  request: { projectId: string; connectorId: string; targetSchemaId?: string; streams?: string[]; limit?: number; columnsByTable?: Record<string, string[]>; batchName?: string }
): Promise<{ batch_id: string; message: string; rows_fetched: number }> {
  const payload: Record<string, unknown> = {
    project_id: request.projectId,
    connector_id: request.connectorId,
    target_schema_id: request.targetSchemaId,
    limit: request.limit,
  };
  if (request.streams?.length) {
    payload.streams = request.streams;
  }
  if (request.columnsByTable && Object.keys(request.columnsByTable).length > 0) {
    payload.columns_by_table = request.columnsByTable;
  }
  if (request.batchName) {
    payload.batch_name = request.batchName;
  }
  const response = await apiClient.post(
    buildUrl('/api/v1/batches/from-connector'),
    payload,
    // Larger connector imports can take a few minutes.
    { timeout: 180000 }, // 3 minutes
  );
  return response.data;
}

export async function getStats(): Promise<DashboardStats> {
  try {
    const response = await apiClient.get<DashboardStats>(buildUrl('/api/v1/stats'));
    return response.data;
  } catch {
    return { total_batches: 0, processed_files: 0 };
  }
}

export async function downloadMappingGuide(batchId: string): Promise<void> {
  const response = await apiClient.get(
    buildUrl(`/api/v1/batches/${batchId}/mapping-guide/download`),
    { responseType: 'blob' }
  );

  const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `mapping-guide-${batchId.substring(0, 8)}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export async function uploadMappingGuide(batchId: string, file: File): Promise<{ updated_count: number }> {
  const response = await apiClient.post(
    buildUrl(`/api/v1/batches/${batchId}/mapping-guide/upload`),
    file,
    { headers: { 'Content-Type': 'application/octet-stream' } }
  );
  return response.data;
}

// Re-export helpers that were class methods but are needed by the ApiService compatibility layer
export { buildEmptyMappingResponse, mapProfileResponse };

// ── Mapping Guide Preview/Apply ────────────────────────────────────────

export interface MappingGuideChangeItem {
  row_id: string;
  target_table: string;
  target_field: string;
  before: Record<string, any>;
  after: Record<string, any>;
  status: 'valid' | 'error' | 'warning';
  change_type: 'added' | 'modified' | 'ai_regenerated';
  errors: string[];
  request_changes?: string | null;
  ai_regenerated: boolean;
}

export interface MappingGuidePreviewResponse {
  batch_id: string;
  recipe_id: string;
  total_changes: number;
  valid_count: number;
  error_count: number;
  warning_count: number;
  changes: MappingGuideChangeItem[];
}

export interface MappingGuideApplyRequest {
  changes: MappingGuideChangeItem[];
  recipe_id: string;
}

export interface MappingGuideApplyResponse {
  status: string;
  updated_count: number;
  skipped_count: number;
}

/**
 * Send the uploaded Excel file to the backend for change detection, validation,
 * and AI regeneration (for rows with request_changes).  Does NOT persist anything.
 */
export async function previewMappingGuide(
  batchId: string,
  file: File
): Promise<MappingGuidePreviewResponse> {
  const response = await apiClient.post(
    buildUrl(`/api/v1/batches/${batchId}/mapping-guide/preview`),
    file,
    { headers: { 'Content-Type': 'application/octet-stream' }, timeout: 300_000 }
  );
  return response.data;
}

/**
 * Commit the confirmed changes from the preview modal to the recipe.
 * Only rows with status 'valid' or 'warning' will be applied.
 */
export async function applyMappingGuideChanges(
  batchId: string,
  payload: MappingGuideApplyRequest
): Promise<MappingGuideApplyResponse> {
  const response = await apiClient.post(
    buildUrl(`/api/v1/batches/${batchId}/mapping-guide/apply`),
    payload
  );
  return response.data;
}

